import { useQuery } from '@apollo/client/react';
import { DimeButton, DimeCardShellSkeleton, DimeDetailPageStates } from '@nudool/design-system';
import { isValidUuidV7 } from '@nudool/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { MapPinIcon } from 'lucide-react';
import { useBreadcrumbLabel } from '../../../components/BreadcrumbLabelsContext.js';
import { assertUuidRouteParams } from '../../../lib/assert-uuid-route-params.js';
import { RouteGuard } from '../../../lib/route-guard.js';
import { LocationDetailView } from '../../../modules/locations/location-detail.js';
import {
  GET_LOCATION,
  type GetLocationQueryResult,
  type GetLocationQueryVariables,
} from '../../../modules/locations/operations.js';

export const Route = createFileRoute('/warehouses/$locationGroupId/$locationId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  component: () => (
    <RouteGuard route="/warehouses/$locationGroupId/$locationId">
      <LocationDetailPage />
    </RouteGuard>
  ),
});

// eslint-disable-next-line react-refresh/only-export-components
function LocationDetailPage() {
  const { locationGroupId, locationId } = Route.useParams();
  const { data, loading, error, refetch } = useQuery<
    GetLocationQueryResult,
    GetLocationQueryVariables
  >(GET_LOCATION, {
    variables: { id: locationId },
    fetchPolicy: 'cache-and-network',
    // The API refuses an id that isn't one as bad input, so no location can match it.
    skip: !isValidUuidV7(locationId),
  });

  const fetchedLocation = data?.location;
  // WHY: locationGroupId is immutable after create, so a mismatch means the URL's two segments were paired from different links (or hand-edited), and must never render one warehouse's breadcrumb over another's location.
  const belongsToUrlGroup = fetchedLocation?.locationGroup?.id === locationGroupId;
  const location = belongsToUrlGroup ? fetchedLocation : undefined;
  useBreadcrumbLabel(locationGroupId, location?.locationGroup?.name);
  useBreadcrumbLabel(locationId, location?.name);

  return (
    <DimeDetailPageStates
      data={location}
      loading={loading}
      error={error}
      entityLabel="Location"
      notFoundIcon={<MapPinIcon />}
      notFoundAction={
        <DimeButton variant="outline" asChild>
          <Link to="/warehouses/$locationGroupId" params={{ locationGroupId }}>
            Back to warehouse
          </Link>
        </DimeButton>
      }
      skeleton={<DimeCardShellSkeleton factCount={2} actionCount={2} tabCount={1} />}
      onRetry={() => void refetch().catch(() => undefined)}
    >
      {(loaded) => (
        <LocationDetailView
          key={loaded.id}
          location={loaded}
          onUpdated={() => {
            void refetch();
          }}
        />
      )}
    </DimeDetailPageStates>
  );
}
